// src/ui/features/calculator/components/UpgradeModal.jsx
import React from "react";
import Card, { cx } from "../../features/ui/Card";
import Pill from "../../features/ui/Pill";
import { CrownIcon } from "../../primatives/Icons";

export default function UpgradeModal({
  open,
  onClose,
  message,
  onViewPricing,

  // Optional overrides
  title = "Upgrade to Pro",
  subtitle = "Unlock the following premium features for life:",
  bullets = [
    "Target Grade Analysis",
    "Module Calculator",
    "Export & Import Grades",
    "Current Grade Distribution",
  ],

  // Optional overrides
  primaryText = "View pricing",
  secondaryText = "Not now",
}) {
  if (!open) return null;

  const ariaLabel = title || "Upgrade to Pro";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 z-0 bg-black/55 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close"
      />

      {/* Modal */}
      <div className="relative w-full max-w-md">
        <Card className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-transparent" />

          {/* Close */}
          <button
            type="button"
            className="
              absolute right-3 top-3 z-20
              inline-flex h-8 w-8 items-center justify-center
              rounded-lg text-neutral-500
              hover:bg-neutral-100 hover:text-neutral-800
              dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-100
            "
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>

          <div className="relative p-6">
            {/* Header */}
            <div className="pr-10">
              <div className="flex items-center gap-2">
                <Pill
                  className={cx(
                    "inline-flex items-center gap-2",
                    "bg-orange-50 text-orange-700 border border-orange-200",
                    "dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-900/40"
                  )}
                >
                  <CrownIcon className="h-4 w-4" />
                  Pro
                </Pill>
              </div>

              <div className="mt-3 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
                {title}
              </div>

              <div className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                {subtitle}
              </div>
            </div>

            {/* Benefits */}
            {bullets?.length ? (
              <ul className="mt-5 space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                {bullets.slice(0, 4).map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500/90" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {/* Message box (for free-limit etc) */}
            {message ? (
              <div
                className="
                  mt-5 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-700
                  dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-300
                "
              >
                {message}
              </div>
            ) : null}

            {/* Actions */}
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                className="
                  flex-1 rounded-xl bg-orange-500 px-4 py-3
                  text-sm font-semibold text-white
                  shadow-sm shadow-orange-500/20
                  hover:bg-orange-400
                  focus:outline-none focus:ring-2 focus:ring-orange-400/60
                "
                onClick={onViewPricing}
              >
                {primaryText}
              </button>

              <button
                type="button"
                className="
                  flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-3
                  text-sm font-semibold text-neutral-800 hover:bg-neutral-100
                  dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-900
                "
                onClick={onClose}
              >
                {secondaryText}
              </button>
            </div>

            <div className="mt-4 text-center text-[11px] text-neutral-500 dark:text-neutral-400">
              Cancel anytime. Secure checkout.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}